/**
 * Palette de commandes (⌘K / Ctrl+K) — sans dépendance externe.
 * Construite sur Radix Dialog + navigation clavier maison.
 *
 * Elle couvre trois natures d'entrée dans une seule liste :
 *   - ACTIONS   : « Encaisser un paiement » — le verbe, pas la page qui le porte
 *   - FICHES    : patients, clients, familles, factures, produits, rendez-vous
 *                 (recherche serveur)
 *   - ALLER À   : les entrées de navigation, filtrées par les mêmes règles
 *                 que la navbar (`isNavItemVisible`)
 *
 * Motif : avec une trentaine d'entrées réparties sur cinq groupes de menu,
 * retrouver une action suppose de savoir dans quel groupe elle vit. La palette
 * supprime cette étape ; le menu reste, pour ceux qui naviguent à la souris.
 *
 * INV-6 : ce qui n'est pas permis n'apparaît pas. Les actions et la navigation
 * sont filtrées côté client pour l'ergonomie ; les fiches le sont côté serveur
 * (`scopes`), qui reste seul juge.
 */
import {
  ArrowRight,
  Calendar,
  Loader2,
  Package,
  Search,
  ShoppingCart,
  UserCog,
  Users,
  Users2,
  type LucideIcon,
} from "lucide-react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";

import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { commandsFor } from "@/config/commands";
import { navDestinations } from "@/config/navigation";
import { useAuthStore } from "@/features/auth/store";
import { useGlobalSearch, SEARCH_MIN_CHARS } from "@/features/search/hooks";
import type { SearchResultType, SearchScopes } from "@/features/search/types";
import { can } from "@/lib/permissions/can";
import { pushRecentCommand, readRecentCommands } from "@/lib/recent-commands";
import { cn } from "@/lib/utils";

const TYPE_ICON: Record<SearchResultType, LucideIcon> = {
  patient: Users,
  client: UserCog,
  family: Users2,
  sale: ShoppingCart,
  product: Package,
  appointment: Calendar,
};

/** Une entrée affichable, quelle que soit son origine. */
interface Entry {
  /** Stable pour les actions/navigation (mémorisable), préfixé pour les fiches. */
  id: string;
  label: string;
  sublabel?: string;
  to: string;
  icon: LucideIcon;
  group: string;
  /** Texte sur lequel porte le filtrage local (actions + navigation). */
  haystack?: string;
}

/** Comparaison insensible à la casse ET aux accents : « souare » trouve « Souaré ». */
function fold(value: string): string {
  return value.normalize("NFD").replace(/[̀-ͯ]/g, "").toLowerCase();
}

export function GlobalSearch({
  triggerClassName,
  triggerLabel,
  onNavigate,
}: {
  triggerClassName?: string;
  triggerLabel?: string;
  onNavigate?: () => void;
} = {}) {
  const me = useAuthStore((s) => s.me);
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [debounced, setDebounced] = useState("");
  const [activeIndex, setActiveIndex] = useState(0);
  const [recent, setRecent] = useState<string[]>([]);
  const listRef = useRef<HTMLDivElement>(null);

  const scopes = useMemo<SearchScopes>(
    () => ({
      patient: can(me, "patients.view"),
      client: can(me, "clients.view"),
      // Les foyers mêlent patients et clients : permission dédiée (users/0017).
      family: can(me, "patients.family.view"),
      sale: can(me, "sales.view"),
      product: can(me, "inventory.view"),
      appointment: can(me, "appointments.view"),
    }),
    [me],
  );

  /** Actions autorisées, converties en entrées de palette. */
  const actionEntries = useMemo<Entry[]>(
    () =>
      commandsFor(me).map((command) => ({
        id: command.id,
        label: command.label,
        to: command.to,
        icon: command.icon,
        group: "Actions",
        haystack: fold(`${command.label} ${command.keywords ?? ""}`),
      })),
    [me],
  );

  /** Navigation — mêmes règles de visibilité que la navbar, jamais dupliquées. */
  const navEntries = useMemo<Entry[]>(
    () =>
      navDestinations(me).map(({ item, trail }) => ({
        id: item.href,
        label: item.label,
        sublabel: trail,
        to: item.href,
        icon: item.icon ?? ArrowRight,
        group: "Aller à",
        haystack: fold(`${item.label} ${trail}`),
      })),
    [me],
  );

  // Ouverture/fermeture au raccourci clavier global ⌘K / Ctrl+K.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        setOpen((v) => !v);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  // Débounce de la saisie (250 ms) avant d'interroger les API.
  useEffect(() => {
    const t = setTimeout(() => setDebounced(query), 250);
    return () => clearTimeout(t);
  }, [query]);

  // Réinitialise l'état à chaque ouverture/fermeture, et relit les récents
  // (ils changent au fil de la session).
  useEffect(() => {
    if (open) {
      setRecent(readRecentCommands());
    } else {
      setQuery("");
      setDebounced("");
    }
    setActiveIndex(0);
  }, [open]);

  const { data: groups = [], isFetching } = useGlobalSearch(debounced, scopes);

  const trimmed = debounced.trim();
  const localMatches = useMemo<Entry[]>(() => {
    const needle = fold(trimmed);
    const pool = [...actionEntries, ...navEntries];
    if (!needle) return [];
    return pool.filter((entry) => entry.haystack?.includes(needle));
  }, [trimmed, actionEntries, navEntries]);

  /** Écran d'accueil : ce que l'utilisateur fait le plus souvent, en tête. */
  const recentEntries = useMemo<Entry[]>(() => {
    const byId = new Map([...actionEntries, ...navEntries].map((e) => [e.id, e]));
    return recent
      .map((id) => byId.get(id))
      .filter((entry): entry is Entry => entry !== undefined)
      .map((entry) => ({ ...entry, group: "Récent" }));
  }, [recent, actionEntries, navEntries]);

  /** Entrées serveur (fiches) — préfixées pour ne jamais finir dans les récents. */
  const entityEntries = useMemo<Entry[]>(
    () =>
      groups.flatMap((group) =>
        group.items.map((item) => ({
          id: `${group.type}:${item.id}`,
          label: item.title,
          sublabel: item.subtitle,
          to: item.to,
          icon: TYPE_ICON[group.type],
          group: group.label,
        })),
      ),
    [groups],
  );

  /**
   * Ordre d'affichage. À vide : les récents, puis les actions — un écran
   * utile plutôt qu'une invite. En recherche : les actions et pages d'abord
   * (réponse immédiate, locale), les fiches ensuite (réponse réseau).
   */
  const entries = useMemo<Entry[]>(() => {
    if (!trimmed) {
      const shown = new Set(recentEntries.map((e) => e.id));
      return [...recentEntries, ...actionEntries.filter((e) => !shown.has(e.id))];
    }
    return [...localMatches, ...entityEntries];
  }, [trimmed, recentEntries, actionEntries, localMatches, entityEntries]);

  useEffect(() => {
    setActiveIndex((i) => (entries.length === 0 ? 0 : Math.min(i, entries.length - 1)));
  }, [entries.length]);

  const go = useCallback(
    (entry: Entry) => {
      setOpen(false);
      onNavigate?.();
      // Ne mémorise que les identifiants sûrs — la fonction filtre elle-même.
      pushRecentCommand(entry.id);
      navigate(entry.to);
    },
    [navigate, onNavigate],
  );

  const onInputKeyDown = (e: React.KeyboardEvent) => {
    if (entries.length === 0) return;
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setActiveIndex((i) => (i + 1) % entries.length);
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActiveIndex((i) => (i - 1 + entries.length) % entries.length);
    } else if (e.key === "Enter") {
      e.preventDefault();
      const entry = entries[activeIndex];
      if (entry) go(entry);
    }
  };

  // Garde l'élément actif visible.
  useEffect(() => {
    const el = listRef.current?.querySelector<HTMLElement>(`[data-idx="${activeIndex}"]`);
    el?.scrollIntoView({ block: "nearest" });
  }, [activeIndex]);

  // « Trop court » ne vaut que pour la recherche serveur : une action se
  // trouve dès la première lettre, il ne faut pas masquer la liste locale.
  const tooShortForEntities =
    trimmed.length > 0 && trimmed.length < SEARCH_MIN_CHARS && localMatches.length === 0;
  const noResults =
    trimmed.length >= SEARCH_MIN_CHARS && !isFetching && entries.length === 0;

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        aria-label="Rechercher ou lancer une action"
        className={
          triggerClassName ??
          "inline-flex items-center gap-2 border border-primary-foreground/30 bg-primary-foreground/10 px-2.5 py-1.5 text-sm text-primary-foreground/80 transition-colors hover:bg-primary-foreground/20 hover:text-primary-foreground focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-foreground/60"
        }
      >
        <Search className="h-4 w-4" />
        {triggerLabel ? (
          <span>{triggerLabel}</span>
        ) : (
          <>
            {/* Libellé dès `2xl` : en dessous, menus + outils ne tenaient pas et
                la barre débordait l'écran (mesuré jusqu'à 1440 px). */}
            <span className="hidden whitespace-nowrap 2xl:inline">Rechercher ou agir…</span>
            <kbd className="ml-1 hidden rounded border border-primary-foreground/30 px-1 text-[10px] font-medium 2xl:inline">
              ⌘K
            </kbd>
          </>
        )}
      </button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent
          // La palette se ferme à Échap et au clic extérieur (override des
          // garde-fous par défaut du DialogContent).
          onEscapeKeyDown={() => undefined}
          onInteractOutside={() => undefined}
          className="left-[50%] top-[12%] max-w-xl translate-x-[-50%] translate-y-0 sm:max-h-[70vh]"
        >
          <DialogTitle className="sr-only">Palette de commandes</DialogTitle>

          <div className="flex items-center gap-2 border-b border-border px-4">
            <Search className="h-4 w-4 shrink-0 text-muted-foreground" />
            <input
              autoFocus
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              onKeyDown={onInputKeyDown}
              placeholder="Que voulez-vous faire ?"
              aria-label="Rechercher une action, une fiche ou une page"
              className="h-12 w-full bg-transparent text-sm outline-none placeholder:text-muted-foreground"
            />
            {isFetching && (
              <Loader2 className="h-4 w-4 shrink-0 animate-spin text-muted-foreground" />
            )}
          </div>

          <div ref={listRef} className="max-h-[60vh] flex-1 overflow-y-auto py-2">
            {tooShortForEntities && (
              <p className="px-4 py-6 text-center text-sm text-muted-foreground">
                Saisissez au moins {SEARCH_MIN_CHARS} caractères pour chercher une fiche.
              </p>
            )}
            {noResults && (
              <p className="px-4 py-6 text-center text-sm text-muted-foreground">
                Aucun résultat pour « {trimmed} ».
              </p>
            )}

            {entries.map((entry, idx) => {
              const Icon = entry.icon;
              const active = idx === activeIndex;
              // En-tête de groupe au premier élément de chaque groupe.
              const isGroupStart = idx === 0 || entries[idx - 1]?.group !== entry.group;
              return (
                <div key={entry.id}>
                  {isGroupStart && (
                    <p className="px-4 py-1 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                      {entry.group}
                    </p>
                  )}
                  <button
                    type="button"
                    data-idx={idx}
                    onClick={() => go(entry)}
                    onMouseMove={() => setActiveIndex(idx)}
                    className={cn(
                      "flex w-full items-center gap-3 px-4 py-2 text-left text-sm transition-colors",
                      active ? "bg-primary/10 text-primary" : "text-foreground hover:bg-muted",
                    )}
                  >
                    <Icon className="h-4 w-4 shrink-0 text-muted-foreground" />
                    <span className="min-w-0 flex-1">
                      <span className="block truncate font-medium">{entry.label}</span>
                      {entry.sublabel && (
                        <span className="block truncate text-xs text-muted-foreground">
                          {entry.sublabel}
                        </span>
                      )}
                    </span>
                    {active && <span className="shrink-0 text-xs text-primary">↵</span>}
                  </button>
                </div>
              );
            })}
          </div>

          <div className="flex items-center gap-4 border-t border-border px-4 py-2 text-[11px] text-muted-foreground">
            <span>↑↓ naviguer</span>
            <span>↵ exécuter</span>
            <span>Échap fermer</span>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
